import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { isValidIsbn } from "@passa-a-pagina/shared";
import { api, ApiError } from "../../lib/api";
import { useAdminSession } from "../../lib/AdminSessionContext";
import { IsbnScanner } from "../../components/IsbnScanner";
import { BookUploadModal } from "../../components/BookUploadModal";
import { PhotoImportModal } from "../../components/PhotoImportModal";
import { UserPicker } from "../../components/UserPicker";
import { IconBarcode } from "../../components/icons";

interface CheckoutLookup {
  bookId: string;
  isbn?: string | null;
  title: string;
  author: string | null;
  coverUrl: string | null;
  bookStatus: "available" | "on_hold" | "checked_out";
  activeHold: {
    holdId: string;
    userEmail: string;
    expiresAt: string;
  } | null;
}

interface SearchResult {
  results: CheckoutLookup[];
}

export function AdminCheckout() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { visitorEmail, setVisitor } = useAdminSession();
  const [isbn, setIsbn] = useState("");
  const [titleQuery, setTitleQuery] = useState("");
  const [lookupIsbn, setLookupIsbn] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [picked, setPicked] = useState<CheckoutLookup | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [directEmail, setDirectEmail] = useState(visitorEmail ?? "");
  const [override, setOverride] = useState(false);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  // Mirror the session pill into the direct-checkout email field so the admin
  // doesn't retype the visitor's email between books in the same visit.
  useEffect(() => {
    setDirectEmail(visitorEmail ?? "");
  }, [visitorEmail]);

  const lookup = useQuery<CheckoutLookup, ApiError>({
    queryKey: ["admin", "checkout-lookup", lookupIsbn],
    queryFn: () =>
      api<CheckoutLookup>(`/api/admin/checkout-lookup/${encodeURIComponent(lookupIsbn)}`),
    enabled: lookupIsbn.length > 0,
    retry: false,
  });

  const search = useQuery<SearchResult, ApiError>({
    queryKey: ["admin", "checkout-search", searchQuery],
    queryFn: () =>
      api<SearchResult>(`/api/admin/checkout-search?q=${encodeURIComponent(searchQuery)}`),
    enabled: searchQuery.length > 0,
    retry: false,
  });

  const data = picked ?? lookup.data;

  function reset() {
    setIsbn("");
    setTitleQuery("");
    setLookupIsbn("");
    setSearchQuery("");
    setPicked(null);
    // Preserve the visit's email between books — admin only clears it via
    // the "terminar visita" pill in the admin header. Read from the context
    // directly instead of trusting the local state (which the success
    // handler may have just updated above; safer to source-of-truth read).
    setDirectEmail(visitorEmail ?? "");
    setOverride(false);
  }

  const collect = useMutation({
    mutationFn: (holdId: string) =>
      api<{ ok: true; bookId: string }>(`/api/admin/holds/${holdId}/collect`, { method: "POST" }),
    onSuccess: () => {
      setFlash({ type: "ok", msg: t("admin.checkoutDone", { title: data?.title ?? "" }) });
      // Persist visitor for the rest of the visit (when the collected hold
      // carried a user email — picking up books they reserved before).
      const collected = data?.activeHold?.userEmail;
      if (collected) setVisitor(collected);
      reset();
      qc.invalidateQueries({ queryKey: ["books"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  const direct = useMutation({
    mutationFn: (input: { bookId: string; userEmail?: string; override?: boolean }) =>
      api<{ ok: true; title: string; userEmail: string; override: boolean }>(
        "/api/admin/checkout-direct",
        { method: "POST", body: input },
      ),
    onSuccess: (res) => {
      setFlash({
        type: "ok",
        msg: res.override
          ? t("admin.directDoneAnon", { title: res.title })
          : t("admin.directDone", { title: res.title, email: res.userEmail }),
      });
      // Persist the recipient on the session — same visitor likely picks up
      // more books in the same trip. Belt-and-suspenders: also reset the
      // direct-email field so the next book's form is guaranteed pre-filled.
      if (!res.override && res.userEmail) {
        setVisitor(res.userEmail);
        setDirectEmail(res.userEmail);
      }
      reset();
      qc.invalidateQueries({ queryKey: ["books"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  function onProcess(e: FormEvent) {
    e.preventDefault();
    setFlash(null);
    setPicked(null);
    setSearchQuery("");
    setLookupIsbn("");

    const isbnClean = isbn.replace(/[-\s]/g, "").toUpperCase();
    const title = titleQuery.trim();

    if (isbnClean) {
      if (!isValidIsbn(isbnClean)) {
        setFlash({ type: "err", msg: t("admin.errIsbn") });
        return;
      }
      setLookupIsbn(isbnClean);
      return;
    }
    if (title.length >= 2) {
      setSearchQuery(title);
      return;
    }
    setFlash({ type: "err", msg: t("admin.searchTooShort") });
  }

  function onDirectSubmit(e: FormEvent) {
    e.preventDefault();
    // Key the handover on bookId, not ISBN: the title-search flow exists
    // precisely for books with no ISBN, and the old ISBN guard silently
    // bailed on those (button appeared to do nothing).
    if (!data?.bookId) return;
    if (override) {
      direct.mutate({ bookId: data.bookId, override: true });
      return;
    }
    // Recipient may be an email OR a name — the server resolves/creates the
    // user either way. Only reject empty/too-short input here.
    const recipient = directEmail.trim();
    if (recipient.length < 2) {
      setFlash({ type: "err", msg: t("admin.directBadEmail") });
      return;
    }
    direct.mutate({ bookId: data.bookId, userEmail: recipient });
  }

  return (
    <section aria-labelledby="checkout-title" className="admin__panel">
      <header className="inventory__head">
        <div>
          <h2 id="checkout-title">{t("admin.checkoutTitle")}</h2>
          <p>{t("admin.checkoutIntro")}</p>
        </div>
        <div className="inventory__head-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setUploadOpen(true)}
          >
            Importar Excel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPhotoOpen(true)}
          >
            Importar foto
          </button>
        </div>
      </header>

      {flash && (
        <div className={`inventory__flash inventory__flash--${flash.type}`} role="alert" aria-live="polite">
          {flash.msg}
        </div>
      )}

      <form onSubmit={onProcess} className="inventory__lookup surface">
        <div className="field">
          <label htmlFor="checkout-isbn">ISBN</label>
          <input
            id="checkout-isbn"
            inputMode="numeric"
            pattern="[0-9-\s]+"
            placeholder="9789720046871"
            value={isbn}
            onChange={(e) => setIsbn(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="checkout-title-q">{t("admin.searchByTitle")}</label>
          <input
            id="checkout-title-q"
            type="text"
            placeholder={t("admin.searchByTitlePlaceholder")}
            value={titleQuery}
            onChange={(e) => setTitleQuery(e.target.value)}
            disabled={isbn.trim().length > 0}
          />
        </div>
        <div className="inventory__lookup-actions">
          <button type="submit" className="btn btn-primary">{t("admin.process")}</button>
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            onClick={() => setScannerOpen(true)}
            aria-label={t("admin.activateCamera")}
            title={t("admin.activateCamera")}
          >
            <IconBarcode />
          </button>
        </div>
        {lookup.isError && (
          <p className="field__error" role="alert">{lookup.error.message}</p>
        )}
        {scannerOpen && (
          <IsbnScanner
            onDetected={(detected) => {
              setIsbn(detected);
              setLookupIsbn(detected);
              setScannerOpen(false);
            }}
            onClose={() => setScannerOpen(false)}
          />
        )}
      </form>

      {/* Search results — title flow with multiple matches */}
      {!picked && search.data && search.data.results.length > 0 && (
        <div className="surface">
          <p className="admin__direct-hint">{t("admin.searchPick")}</p>
          <ul className="search-results">
            {search.data.results.map((r) => (
              <li key={r.bookId}>
                <button type="button" onClick={() => setPicked(r)}>
                  <span className="search-results__title">{r.title}</span>
                  {r.author && <span className="search-results__sub">{r.author}</span>}
                  <span className="search-results__status">
                    {r.activeHold ? `→ ${r.activeHold.userEmail}` : t(`book.status${r.bookStatus.charAt(0).toUpperCase()}${r.bookStatus.slice(1).replace("_", "")}`, { defaultValue: r.bookStatus })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!picked && search.data && search.data.results.length === 0 && (
        <p className="field__error" role="alert">{t("admin.searchEmpty")}</p>
      )}

      {data && (
        <div className="surface admin__confirm">
          <div className="inventory__preview">
            {data.coverUrl ? (
              <img src={data.coverUrl} alt="" className="inventory__cover" />
            ) : (
              <div className="inventory__cover inventory__cover--empty" aria-hidden="true">📖</div>
            )}
            <div>
              <h3>{data.title}</h3>
              {data.author && <p>{data.author}</p>}
              <p className="inventory__isbn">
                <strong>{t("admin.bookStatus")}:</strong> {t(`book.status${data.bookStatus.charAt(0).toUpperCase()}${data.bookStatus.slice(1).replace("_", "")}`, { defaultValue: data.bookStatus })}
              </p>
            </div>
          </div>

          {data.activeHold ? (
            <>
              <p>
                <strong>{t("admin.heldBy")}:</strong> {data.activeHold.userEmail}
                <br />
                {t("admin.holdExpires")}: {new Date(data.activeHold.expiresAt).toLocaleString()}
              </p>
              <div className="inventory__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={collect.isPending}
                  onClick={() => collect.mutate(data.activeHold!.holdId)}
                >
                  {collect.isPending ? t("admin.checkoutSubmitting") : t("admin.checkoutConfirm")}
                </button>
                <button type="button" className="btn btn-secondary" onClick={reset}>
                  {t("common.cancel")}
                </button>
              </div>
            </>
          ) : data.bookStatus === "available" ? (
            <form onSubmit={onDirectSubmit} className="admin__direct">
              <p className="admin__direct-hint">{t("admin.directHint")}</p>

              {!override && (
                <div className="field">
                  <label htmlFor="direct-email">{t("admin.directEmailLabel")} (email ou nome)</label>
                  <UserPicker
                    inputId="direct-email"
                    value={directEmail}
                    onChange={setDirectEmail}
                    placeholder={t("admin.checkoutDirectPlaceholder")}
                    required
                    disabled={direct.isPending}
                  />
                </div>
              )}

              <label className="admin__override">
                <input
                  type="checkbox"
                  checked={override}
                  onChange={(e) => setOverride(e.target.checked)}
                  disabled={direct.isPending}
                />
                <span>{t("admin.directOverride")}</span>
              </label>

              <div className="inventory__actions">
                <button type="submit" className="btn btn-primary" disabled={direct.isPending}>
                  {direct.isPending ? t("admin.directSubmitting") : t("admin.directSubmit")}
                </button>
                <button type="button" className="btn btn-secondary" onClick={reset}>
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <p className="field__error">
              {t("admin.bookAlreadyOut")}
            </p>
          )}
        </div>
      )}

      {uploadOpen && (
        <BookUploadModal mode="checkout" onClose={() => setUploadOpen(false)} />
      )}
      {photoOpen && (
        <PhotoImportModal mode="checkout" onClose={() => setPhotoOpen(false)} />
      )}
    </section>
  );
}
